import { animated, useSpring, useSprings } from '@react-spring/web';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { MediaAssets } from '../services/ofertas-service/models/ofertas';

const Container = styled.div`
	width: 100vw;
	overflow: hidden;
	height: 100%;
	position: relative;
	z-index: 0;
`;

const CarImagesRow = styled(animated.div)`
	display: flex;
	flex-direction: row;
	align-items: center;
	position: relative;
	height: 100%;
	white-space: nowrap;
`;

const CarImage = styled(animated.img)`
	height: 100%;
	flex-shrink: 0;
	object-fit: contain;
	width: clamp(150px, 42vw, 925px);

	@media screen and (min-width: 962px) and (max-width: 1279px) {
		height: 265px;
	}
`;

export const Carrousel = ({
	mediaAssets,
}: {
	mediaAssets: MediaAssets;
}): React.ReactElement => {
	const turntable = [
		'D6Turntable1',
		'D6Turntable3',
		'D6Turntable5',
		'D6Turntable6',
		'D6Turntable7',
		'D6Turntable9',
		'D6Turntable12',
	];

	const carImages = turntable
		.map((t) => mediaAssets[t])
		.filter((image) => image !== null && image !== undefined);

	const loopImages = [...carImages, ...carImages, ...carImages];
	const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
	const [imageWidth, setImageWidth] = useState(0);
	const imageRef = useRef<HTMLImageElement | null>(null);

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		if (!imageRef.current) return;
		const observer = new ResizeObserver((entries) => {
			const width = entries[0].contentRect.width;
			setImageWidth(width);
		});
		observer.observe(imageRef.current);
		return () => observer.disconnect();
	}, [imageRef.current]);

	const totalWidth = loopImages.length * imageWidth;

	const startIndex = carImages.length;
	const initialOffset =
		-startIndex * imageWidth + (windowWidth / 2 - imageWidth / 2 - 120);
	const targetOffset = initialOffset - imageWidth * carImages.length;

	const [{ x }, api] = useSpring(() => ({
		x: initialOffset,
	}));

	useEffect(() => {
		if (!imageWidth) return;

		const startIndex = carImages.length;
		const newInitialOffset =
			-startIndex * imageWidth + (windowWidth / 2 - imageWidth / 2 - 120);
		const newTargetOffset = newInitialOffset - imageWidth * carImages.length;

		let cancelled = false;

		const animate = () => {
			if (cancelled) return;
			api.start({
				from: { x: newInitialOffset },
				to: { x: newTargetOffset },
				config: { duration: 120000 },
				onRest: () => {
					animate();
				},
			});
		};

		animate();

		return () => {
			cancelled = true;
		};
	}, [api, imageWidth, windowWidth, carImages.length]);

	const menuWidth = 250;
	const center = windowWidth / 2;
	const fadeStart = center - menuWidth - imageWidth / 2;
	const fadeEnd = fadeStart + 150;

	const springs = useSprings(
		loopImages.length,
		loopImages.map((_, i) => ({
			opacity: x.to((val) => {
				const imageCenterX = i * imageWidth + val + imageWidth / 2;
				const distance = center - imageCenterX;

				if (distance < fadeStart) return 1;
				if (distance > fadeEnd) return 0.25;

				const fadeRange = fadeEnd - fadeStart;
				const relative = (distance - fadeStart) / fadeRange;
				return 1 - relative * 0.75;
			}),
		})),
	);

	return (
		<Container>
			<CarImagesRow
				style={{
					width: `${totalWidth}px`,
					transform: x.to((val) => `translateX(${val}px)`),
				}}
			>
				{loopImages.map((image, index) => (
					<CarImage
						key={index}
						ref={index === 0 ? imageRef : null}
						src={image}
						style={{
							opacity: springs[index].opacity,
						}}
					/>
				))}
			</CarImagesRow>
		</Container>
	);
};
